import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { api } from "../../utils/api";
import { API_ENDPOINTS } from "../../constants/api";
import ClientFileUpload from "./ClientFileUpload";
import ClientSlidesView from "./ClientSlidesView";

import "../AdminConsole.css";

function ClientManagement() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [deletingClient, setDeletingClient] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null); // This will hold the client ID
  const [viewMode, setViewMode] = useState("list"); // list, slides, upload

  const fetchClientsData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(API_ENDPOINTS.CLIENTS.GET_ALL);
      const clientsData = response.data.clients || [];
      setClients(clientsData);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setError("Failed to fetch clients data");
      toast.error("Failed to load clients data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientsData();
  }, []);

  // Delete client
  const handleDeleteClient = async (clientId) => {
    setDeletingClient(clientId);

    try {
      // Find the client to get additional info for the delete request
      const clientToDelete = clients.find((client) => client._id === clientId);

      if (!clientToDelete) {
        toast.error("Client not found");
        setDeletingClient(null);
        setConfirmingDelete(null);
        return;
      }

      const response = await api.post(
        `${API_ENDPOINTS.CLIENTS.DELETE}/${clientId}`
      );

      if (response.data.success) {
        toast.success(
          `Client "${clientToDelete.client_name}" deleted successfully!`
        );
        // Update local state to remove the deleted client
        setClients(clients.filter((client) => client._id !== clientId));
      } else {
        toast.error(response.data.message || "Failed to delete client");
      }
    } catch (error) {
      console.error("Error deleting client:", error);
      toast.error(error.response?.data?.message || "Failed to delete client");
    } finally {
      setDeletingClient(null);
      setConfirmingDelete(null);
    }
  };

  // Handle client selection
  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setViewMode("slides");
  };

  // Handle upload completion from ClientFileUpload component
  const handleUploadComplete = async (clientName) => {
    // Refresh clients list after successful upload
    await fetchClientsData();
    setViewMode("list");
    toast.success(`Client "${clientName}" added successfully!`);
  };

  // Handle back navigation from upload view
  const handleBackFromUpload = () => {
    setViewMode("list");
  };

  // Handle client update after slide operations
  const handleClientUpdate = (updatedClientData) => {
    setSelectedClient(updatedClientData);
    setClients((prevClients) => {
      return prevClients.map((client) =>
        client._id === updatedClientData._id ? updatedClientData : client
      );
    });
  };

  // Handle back navigation from slides view
  const handleBackFromSlides = () => {
    setViewMode("list");
    setSelectedClient(null);
  };

  // Render client list
  const renderClientList = () => (
    <div className="client-list">
      <div className="section-header">
        <h2>Client Management</h2>
        <button
          className="btn-primary"
          onClick={() => setViewMode("upload")}>
          <span>📤</span> Upload Client Files
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading clients...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>Error: {error}</p>
          <button
            onClick={fetchClientsData}
            className="btn-secondary">
            Retry
          </button>
        </div>
      ) : (
        <div className="clients-grid">
          {clients.length === 0 ? (
            <div className="empty-state">
              <h3>No Clients Found</h3>
              <p>
                Add your first client to get started with managing their slides
                and data.
              </p>
            </div>
          ) : (
            clients.map((client) => (
              <div
                key={client._id}
                className="client-card">
                <div className="client-header">
                  <h3>{client.client_name}</h3>
                  <div className="client-actions">
                    <button
                      onClick={() => handleSelectClient(client)}
                      className="btn-view"
                      title="View Slides">
                      📋
                    </button>
                    {confirmingDelete === client._id ? (
                      <div className="delete-confirmation-inline">
                        <span className="confirm-text">Delete?</span>
                        <div className="confirm-buttons">
                          <button
                            className="confirm-yes-btn"
                            onClick={() => handleDeleteClient(client._id)}
                            disabled={deletingClient === client._id}
                            title="Yes, delete client">
                            {deletingClient === client._id ? "⏳" : "✓"}
                          </button>
                          <button
                            className="confirm-no-btn"
                            onClick={() => setConfirmingDelete(null)}
                            disabled={deletingClient === client._id}
                            title="No, cancel deletion">
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmingDelete(client._id)}
                        className="btn-delete"
                        title="Delete Client"
                        disabled={confirmingDelete !== null}>
                        🗑️
                      </button>
                    )}
                  </div>
                </div>

                <div className="client-info">
                  {client.client_brief && (
                    <p>
                      <strong>Brief:</strong> {client.client_brief}
                    </p>
                  )}
                </div>

                <div className="client-stats">
                  <span className="stat">
                    � {client.client_slides?.length || 0} Total Slides
                  </span>
                  <span className="stat">
                    � Created:{" "}
                    {client.createdAt
                      ? new Date(client.createdAt).toLocaleDateString()
                      : "N/A"}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="client-management">
      {viewMode === "list" && renderClientList()}
      {viewMode === "slides" && selectedClient && (
        <ClientSlidesView
          client={selectedClient}
          onBack={handleBackFromSlides}
          onClientUpdate={handleClientUpdate}
        />
      )}
      {viewMode === "upload" && (
        <ClientFileUpload
          onBack={handleBackFromUpload}
          onUploadComplete={handleUploadComplete}
        />
      )}
    </div>
  );
}

export default ClientManagement;
